import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDataGrid, { Column, renderSortIcon, renderSortPriority, SortColumn } from "react-data-grid";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { PLAElementRecord, PLAElementTypeDefinition, PLAProject } from "./PLAState";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";
import { formatPLAAttributeValue } from "./PLAFormatting";
import { PLAAlertMark } from "./PLAAlertMark";
import "./PLATable.css";

export type PLATableRef = {
   scrollToRow: (elementId: string, focusRow?: boolean) => void;
};

type PLATableRow = PLAElementRecord & {
   __alerts: string[];
   __alertsByAttributeId: Record<string, string[]>;
   __calculated: Record<string, any>;
};

type PLATablePreferences = {
   visibleColumns?: Record<string, boolean>;
   sortColumns?: readonly SortColumn[];
   columnWidths?: Record<string, number>;
};

type Props = {
   project: PLAProject;
   elementTypeId: string;
   elementType: PLAElementTypeDefinition;
   elements: PLAElementRecord[];
   allElements: PLAElementRecord[];
   relations: any[];
   selectedElementId?: string;
   readOnly: boolean;
   onRowClick: (element: PLAElementRecord) => void;
   onAddElement: () => void;
   onOpenElement: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteElement: (element: PLAElementRecord) => void;
};

function compareValues(a: any, b: any) {
   if (a === b) return 0;
   if (a === undefined || a === null) return -1;
   if (b === undefined || b === null) return 1;
   if (typeof a === "number" && typeof b === "number") return a - b;
   return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function getAttributeDisplay(row: PLATableRow, attributeId: string) {
   if (Object.prototype.hasOwnProperty.call(row.__calculated, attributeId)) return row.__calculated[attributeId];
   const value = row.attributes[attributeId];
   return value?.mode === "constant" ? value.value : value?.expression || "";
}

function RowActionButton(props: { title: string; icon: "edit" | "mapLink" | "delete"; onClick: () => void }) {
   return (
      <GlassButton
         className="pla-row-action"
         title={props.title}
         aria-label={props.title}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
      >
         <MIBIcon icon={props.icon} />
      </GlassButton>
   );
}

function PLAAttributeHeader(props: { name: string; description?: string; sortDirection?: "ASC" | "DESC"; priority?: number; sortable?: boolean }) {
   const description = props.description?.trim();
   return (
      <span className="pla-attribute-header" title={description || undefined}>
         <span className={description ? "pla-attribute-header__name pla-attribute-header__name--has-description" : "pla-attribute-header__name"}>{props.name}</span>
         {props.sortable && (
            <span className="pla-attribute-header__sort">
               {renderSortIcon({ sortDirection: props.sortDirection })}
               {renderSortPriority({ priority: props.priority })}
            </span>
         )}
      </span>
   );
}

function initialVisibleColumns(elementType: PLAElementTypeDefinition): Record<string, boolean> {
   const visibleColumns: Record<string, boolean> = { name: true };
   getSortedPLAAttributeEntries(elementType.attributes).forEach(([attributeId, definition]) => {
      if (isElementNameAttribute(attributeId, definition.name)) return;
      visibleColumns[`attr:${attributeId}`] = !definition.hideAsDefault;
   });
   return visibleColumns;
}

function getColumnOptions(elementType: PLAElementTypeDefinition) {
   return [
      { key: "name", name: Localization.getText("Name") },
      ...getSortedPLAAttributeEntries(elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({ key: `attr:${attributeId}`, name: definition.name || attributeId }))
   ];
}

function getPLATablePreferencesKey(userId: string, projectId: string, elementTypeId: string) {
   return `PLAState-tablePreferences-${userId}-${projectId}-${elementTypeId}`;
}

function readPLATablePreferences(key: string): PLATablePreferences {
   try {
      return JSON.parse(localStorage.getItem(key) || "{}") || {};
   } catch {
      return {};
   }
}

function normalizeVisibleColumns(elementType: PLAElementTypeDefinition, stored?: Record<string, boolean>): Record<string, boolean> {
   const defaults = initialVisibleColumns(elementType);
   const validKeys = new Set(Object.keys(defaults));
   return Object.entries(defaults).reduce<Record<string, boolean>>((result, [key, defaultValue]) => {
      result[key] = validKeys.has(key) && typeof stored?.[key] === "boolean" ? stored[key] : defaultValue;
      return result;
   }, {});
}

function normalizeSortColumns(elementType: PLAElementTypeDefinition, stored?: readonly SortColumn[]): SortColumn[] {
   if (!Array.isArray(stored)) return [];
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return stored.filter((sort) => validKeys.has(sort.columnKey) && (sort.direction === "ASC" || sort.direction === "DESC"));
}

function normalizeColumnWidths(elementType: PLAElementTypeDefinition, stored?: Record<string, number>): Record<string, number> {
   if (!stored || typeof stored !== "object") return {};
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return Object.entries(stored).reduce<Record<string, number>>((result, [key, width]) => {
      if (validKeys.has(key) && typeof width === "number" && Number.isFinite(width) && width > 0) result[key] = width;
      return result;
   }, {});
}

function isElementNameAttribute(attributeId: string, attributeName?: string) {
   const reservedNames = new Set(["name", Localization.getText("Name").trim().toLowerCase()]);
   return reservedNames.has(attributeId.trim().toLowerCase()) || reservedNames.has((attributeName || "").trim().toLowerCase());
}

export const PLATable = forwardRef<PLATableRef, Props>((props, ref) => {
   const { state: sessionState } = useContext(SessionContext);
   const containerRef = useRef<HTMLDivElement | null>(null);
   const gridRef = useRef<any>(null);
   const skipNextPreferencesPersist = useRef(false);
   const storageUserId = sessionState.userName || sessionState.userRef || "anonymous";
   const preferencesKey = useMemo(() => getPLATablePreferencesKey(storageUserId, props.project.id, props.elementTypeId), [storageUserId, props.project.id, props.elementTypeId]);
   const [filter, setFilter] = useState("");
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>(() => normalizeSortColumns(props.elementType, readPLATablePreferences(preferencesKey).sortColumns));
   const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => normalizeVisibleColumns(props.elementType, readPLATablePreferences(preferencesKey).visibleColumns));
   const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => normalizeColumnWidths(props.elementType, readPLATablePreferences(preferencesKey).columnWidths));

   useEffect(() => {
      const stored = readPLATablePreferences(preferencesKey);
      skipNextPreferencesPersist.current = true;
      setVisibleColumns(normalizeVisibleColumns(props.elementType, stored.visibleColumns));
      setSortColumns(normalizeSortColumns(props.elementType, stored.sortColumns));
      setColumnWidths(normalizeColumnWidths(props.elementType, stored.columnWidths));
   }, [preferencesKey, props.elementType]);

   useEffect(() => {
      if (skipNextPreferencesPersist.current) {
         skipNextPreferencesPersist.current = false;
         return;
      }
      localStorage.setItem(preferencesKey, JSON.stringify({ visibleColumns, sortColumns, columnWidths } satisfies PLATablePreferences));
   }, [preferencesKey, sortColumns, visibleColumns, columnWidths]);

   const rows: PLATableRow[] = useMemo(() => {
      return props.elements.map((element) => {
         let calculated: Record<string, any> = {};
         let alerts: string[] = [];
         let alertsByAttributeId: Record<string, string[]> = {};
         try {
            calculated = resolvePLACalculatedAttributes(element, props.project, props.allElements, props.relations, { includeErrors: true });
            const alertDetails = getPLAAlertDetails(element, props.project, props.allElements, props.relations);
            alerts = alertDetails.map((alert) => alert.message);
            alertsByAttributeId = alertDetails.reduce<Record<string, string[]>>((acc, alert) => {
               acc[alert.attributeId] = [...(acc[alert.attributeId] || []), alert.message];
               return acc;
            }, {});
         } catch (error: any) {
            alerts = [error.message || String(error)];
         }
         return { ...element, __alerts: alerts, __alertsByAttributeId: alertsByAttributeId, __calculated: calculated };
      });
   }, [props.elements, props.project, props.allElements, props.relations]);

   const filteredRows = useMemo(() => {
      const lowerFilter = filter.trim().toLowerCase();
      const visible = lowerFilter
         ? rows.filter((row) => JSON.stringify({ name: row.name, attributes: row.attributes, calculated: row.__calculated }).toLowerCase().includes(lowerFilter))
         : rows;
      if (sortColumns.length === 0) return visible;
      return [...visible].sort((a, b) => {
         for (const sort of sortColumns) {
            const aValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(a, sort.columnKey.slice(5)) : (a as any)[sort.columnKey];
            const bValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(b, sort.columnKey.slice(5)) : (b as any)[sort.columnKey];
            const result = compareValues(aValue, bValue);
            if (result !== 0) return sort.direction === "ASC" ? result : -result;
         }
         return 0;
      });
   }, [rows, filter, sortColumns]);

   const focusSelectedRow = useCallback(() => {
      window.setTimeout(() => {
         const selectedCell = containerRef.current?.querySelector<HTMLElement>(".rdg-row-selected .rdg-cell");
         if (!selectedCell) return;
         selectedCell.tabIndex = -1;
         selectedCell.focus();
      }, 0);
   }, []);

   const scrollToRow = useCallback((elementId: string, focusRow = false) => {
      const rowIndex = filteredRows.findIndex((row) => row.id === elementId);
      if (rowIndex >= 0) {
         gridRef.current?.scrollToCell?.({ rowIdx: rowIndex, idx: 0 });
         if (focusRow) focusSelectedRow();
      }
   }, [filteredRows, focusSelectedRow]);

   useImperativeHandle(ref, () => ({ scrollToRow }), [scrollToRow]);

   useEffect(() => {
      if (props.selectedElementId) scrollToRow(props.selectedElementId, true);
   }, [props.selectedElementId, scrollToRow]);

   const setColumnVisible = useCallback((columnKey: string, visible: boolean) => {
      setVisibleColumns((prev) => ({ ...prev, [columnKey]: visible }));
      if (!visible) {
         setColumnWidths((prev) => {
            if (prev[columnKey] === undefined) return prev;
            const next = { ...prev };
            delete next[columnKey];
            return next;
         });
      }
   }, []);

   const columns: Column<PLATableRow>[] = useMemo(() => {
      const attributeColumns: Column<PLATableRow>[] = getSortedPLAAttributeEntries(props.elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({
            key: `attr:${attributeId}`,
            name: definition.name || attributeId,
            width: columnWidths[`attr:${attributeId}`],
            sortable: true,
            resizable: true,
            renderHeaderCell: ({ column, sortDirection, priority }) => (
               <PLAAttributeHeader
                  name={definition.name || attributeId}
                  description={definition.description}
                  sortDirection={sortDirection}
                  priority={priority}
                  sortable={column.sortable}
               />
            ),
            renderCell: ({ row }) => {
               const alertMessages = row.__alertsByAttributeId[attributeId] || [];
               return <span title={alertMessages.join("\n") || undefined}>{formatPLAAttributeValue(getAttributeDisplay(row, attributeId), definition)}</span>;
            },
            cellClass: (row) => (row.__alertsByAttributeId[attributeId] || []).length ? "pla-alert-cell" : undefined
         }));
      return [
         {
            key: "alert",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__alerts.length ? <PLAAlertMark messages={row.__alerts} placement="right" /> : null
         },
         ...(visibleColumns.name ? [{
            key: "name",
            name: Localization.getText("Name"),
            width: columnWidths.name,
            sortable: true,
            resizable: true,
            renderCell: ({ row }) => <span className="pla-name-cell">{row.name}</span>
         } as Column<PLATableRow>] : []),
         ...attributeColumns.filter((column) => visibleColumns[column.key] !== false),
         {
            key: "actions",
            name: Localization.getText("Actions"),
            width: props.readOnly ? 54 : 120,
            renderCell: ({ row }) => (
               <div className="pla-row-actions">
                  <RowActionButton title={Localization.getText("Open element")} icon="edit" onClick={() => props.onOpenElement(row)} />
                  {!props.readOnly && <RowActionButton title={Localization.getText("Create relation")} icon="mapLink" onClick={() => props.onCreateRelation(row)} />}
                  {!props.readOnly && <RowActionButton title={Localization.getText("Delete element")} icon="delete" onClick={() => props.onDeleteElement(row)} />}
               </div>
            )
         }
      ];
   }, [props.elementType, props.readOnly, props.onOpenElement, props.onCreateRelation, props.onDeleteElement, visibleColumns, columnWidths]);

   const handleColumnResize = useCallback((idx: number, width: number) => {
      const columnKey = columns[idx]?.key;
      if (!columnKey || columnKey === "alert" || columnKey === "actions") return;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: width }));
   }, [columns]);

   const columnOptions = useMemo(() => getColumnOptions(props.elementType), [props.elementType]);

   return (
      <div ref={containerRef} className="pla-table" data-testid="pla-table">
         <div className="pla-toolbar">
            <div className="pla-toolbar__row">
               <input
                  className="pla-search"
                  aria-label={Localization.getText("Filter")}
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder={Localization.getText("Search in name, text or attributes...")}
               />
               <details className="pla-columns pla-menu" style={{ position: "relative" }}>
                  <summary className="pla-btn pla-btn--ghost">{Localization.getText("Columns")}</summary>
                  <div className="pla-columns__options">
                     {columnOptions.map((column) => (
                        <label key={column.key}>
                           <input
                              type="checkbox"
                              checked={visibleColumns[column.key] !== false}
                              onChange={(event) => setColumnVisible(column.key, event.target.checked)}
                           />
                           {column.name}
                        </label>
                     ))}
                  </div>
               </details>
               {!props.readOnly && <GlassButton className="pla-btn pla-btn--solid" onClick={props.onAddElement}>{Localization.getText("Add element")}</GlassButton>}
            </div>
         </div>
         <div className="pla-table-wrap">
            <ReactDataGrid
               ref={gridRef}
               columns={columns}
               rows={filteredRows}
               className="rdg-light pla-data-grid"
               sortColumns={sortColumns}
               onSortColumnsChange={setSortColumns}
               onColumnResize={handleColumnResize}
               rowKeyGetter={(row) => row.id}
               rowClass={(row) => row.id === props.selectedElementId ? "rdg-row-selected" : undefined}
               onCellClick={({ row }) => props.onRowClick(row)}
               style={{ flex: "1 1 auto", minHeight: 0, height: "100%", blockSize: "100%" }}
            />
         </div>
      </div>
   );
});
